import { ApiRequestFatalError } from './api-request-fatal.error';
import { HttpStatus } from './enums/http-status.enum';
import { normalizeStringifiedFetchMessage } from './network-fetch-error.util';

export type BootstrapErrorClassification = 'auth' | 'transient';

/**
 * Some errors carry only a stringified fetch Error like "Error: Unauthorized"
 * — the HTTP statusText is the only signal. Matched against the whole message
 * (case-insensitive) so ordinary error copy that merely contains the word is
 * never mistaken for a credential rejection. Note statusText is empty over
 * HTTP/2, so this is a best-effort fallback; bootstrap-critical fetches throw
 * InternalError with a numeric status instead.
 */
const AUTH_STATUS_TEXTS: ReadonlySet<string> = new Set(['unauthorized', 'forbidden']);

function matchesAuthStatusText(message: string): boolean {
  return AUTH_STATUS_TEXTS.has(normalizeStringifiedFetchMessage(message));
}

function extractHttpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  // InternalError exposes `status`; NestJS-style payloads expose `statusCode`.
  const { status, statusCode } = error as { status?: unknown; statusCode?: unknown };
  if (typeof status === 'number') return status;
  if (typeof statusCode === 'number') return statusCode;
  return undefined;
}

/**
 * Decides whether a bootstrap failure means the session is genuinely unusable
 * ('auth') or the dashboard just couldn't load right now ('transient').
 *
 * Only definitive credential rejections warrant a logout: a 401 that survived
 * the token-refresh retry, a 403, or an ApiRequestFatalError from the auth
 * exchanges. Everything else — network failures, 5xx, rate limits, timeouts,
 * malformed responses — defaults to 'transient', because logging out cannot
 * fix those and destroys a valid session, bouncing the user through UI2's
 * false "plan expired" screen.
 */
export function classifyBootstrapError(error: unknown): BootstrapErrorClassification {
  // Thrown by the auth persistence layer only after the server definitively
  // rejected our credentials — retrying cannot change the outcome.
  if (error instanceof ApiRequestFatalError) {
    return 'auth';
  }

  const status = extractHttpStatus(error);
  if (status === HttpStatus.UNAUTHORIZED || status === HttpStatus.FORBIDDEN) {
    return 'auth';
  }

  if (error instanceof Error && matchesAuthStatusText(error.message)) {
    return 'auth';
  }

  return 'transient';
}
